<template>
  <div class="seo-page">
    <section class="seo-hero">
      <span class="seo-badge">Privacy Policy</span>
      <h1>Privacy Policy</h1>
      <p>
        steamplayti.me shows Steam players how much they played each day, game by game. This page explains what
        data that takes, where it comes from, who else handles it, and how to have it deleted.
      </p>
      <p class="policy-updated">Last updated: {{ lastUpdated }}</p>
    </section>

    <section class="seo-section">
      <h2>What we collect</h2>
      <ul class="policy-list">
        <li>
          <strong>Public Steam data.</strong> For a profile someone looks up or signs in with: the Steam ID,
          display name and avatar, the list of owned games with their playtime, and unlocked achievements. We
          read this from Valve's public Steam Web API, and only while the profile's game details are public.
          We never see or store your Steam password.
        </li>
        <li>
          <strong>Daily playtime snapshots.</strong> Once a day we save each tracked game's total playtime, so the
          site can show how much you played on each day.
        </li>
        <li>
          <strong>Email address (optional).</strong> Only if you claim your profile. We use it to confirm the
          claim and to send the playtime emails described below.
        </li>
        <li>
          <strong>Technical data.</strong> Your IP address is used briefly to rate-limit requests, and our
          hosting and CDN keep standard server logs for security.
        </li>
      </ul>
    </section>

    <section class="seo-section">
      <h2>Cookies and local storage</h2>
      <ul class="policy-list">
        <li>
          <strong>Sign-in cookie.</strong> After you sign in through Steam, a cookie keeps you signed in. It
          holds no password.
        </li>
        <li>
          <strong>Local storage.</strong> Your browser stores your language choice and whether you still have a
          profile claim to finish. This stays on your device.
        </li>
        <li>
          <strong>Analytics.</strong> We use Google Analytics to understand how the site is used (pages
          visited, device type, rough location). Google sets its own cookies for this.
        </li>
        <li>
          <strong>Advertising.</strong> We use Google AdSense to show ads. Google and its partners may use cookies
          to show ads based on your visits to this and other websites. You can turn off personalised ads in
          <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google Ads Settings</a>,
          and read how Google uses data at
          <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">How Google uses information from sites that use its services</a>.
        </li>
      </ul>
    </section>

    <section class="seo-section">
      <h2>How we use it</h2>
      <ul class="policy-list">
        <li>To show the playtime dashboard, charts, streaks and achievement history.</li>
        <li>
          To send a one-off "your data is ready" email and a weekly playtime recap to claimed profiles. Every
          email has an unsubscribe link.
        </li>
        <li>To keep the service running and protect it from abuse.</li>
      </ul>
      <p>We do not sell your personal data.</p>
    </section>

    <section class="seo-section">
      <h2>Who can see a dashboard</h2>
      <p>
        A dashboard is built only from data Steam already shows publicly, so anyone who knows a Steam ID or
        profile link can open it, just like the Steam profile itself. Making your Steam game details private
        stops us from reading new data. A profile that is unclaimed and stays private is deleted
        automatically.
      </p>
    </section>

    <section class="seo-section">
      <h2>Services that process data for us</h2>
      <ul class="policy-list">
        <li><strong>Valve (Steam Web API)</strong>: the source of the profile, game and achievement data.</li>
        <li><strong>Cloudflare</strong>: content delivery and protection against attacks.</li>
        <li><strong>Oracle Cloud</strong>: hosts our servers and database.</li>
        <li><strong>Brevo</strong>: sends our emails.</li>
        <li><strong>Google</strong>: Analytics and AdSense, as described above.</li>
      </ul>
    </section>

    <section class="seo-section">
      <h2>How long we keep it</h2>
      <p>
        Data for profiles nobody returns to is deleted automatically after a period of inactivity. Claimed
        profiles are kept until their owner asks for deletion. Database backups age out on their own within
        about a month.
      </p>
    </section>

    <section class="seo-section">
      <h2>Your rights and deleting your data</h2>
      <p>
        You can ask to see, correct or delete the data we hold about your profile. Email
        <a href="mailto:hello@steamplayti.me">hello@steamplayti.me</a> with your Steam profile link. Deletion removes
        your profile, playtime history, achievements, email address and share image. To stop emails only, use
        the unsubscribe link in any email.
      </p>
    </section>

    <section class="seo-section">
      <h2>Children</h2>
      <p>
        steamplayti.me is not directed at children under 13, the minimum age for a Steam account. We do not
        knowingly collect data from them.
      </p>
    </section>

    <section class="seo-section">
      <h2>Changes and contact</h2>
      <p>
        If this policy changes, we update this page and the date at the top. Questions? Email
        <a href="mailto:hello@steamplayti.me">hello@steamplayti.me</a>. More about the project is on the
        <router-link to="/about/">About page</router-link>.
      </p>
      <p>steamplayti.me is an independent project and is not affiliated with Valve or Steam.</p>
    </section>
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';

const CANONICAL = 'https://steamplayti.me/privacy/';
const META_DESCRIPTION = 'What data steamplayti.me collects, where it comes from, which services process it, how long it is kept, and how to have it deleted.';

const META = {
  title: 'Privacy Policy | steamplayti.me',
  description: META_DESCRIPTION,
  canonical: CANONICAL,
  ogTitle: 'Privacy Policy | steamplayti.me',
  ogDescription: META_DESCRIPTION,
  ogUrl: CANONICAL,
  twitterTitle: 'Privacy Policy | steamplayti.me',
  twitterDescription: META_DESCRIPTION,
  structuredData: pageStructuredData({
    name: 'Privacy Policy',
    description: META_DESCRIPTION,
    canonical: CANONICAL,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: CANONICAL },
    ],
  }),
};

export default {
  name: 'PrivacyPage',
  data() {
    return { lastUpdated: 'October 10, 2026' };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>
<style scoped>
.policy-updated {
  font-size: 0.9rem;
}

.policy-list {
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--muted);
  line-height: 1.6;
}

.policy-list strong {
  color: var(--text);
}

.seo-section a {
  color: var(--primary);
}
</style>
